/**
 * HijriHesitation — pure helper for "Yawm al-Shakk" (day of hesitation) markers.
 *
 * Per RESEARCH-YAWM-AL-SHAKK-2026-05-15.md (Searchov fiqh + UX validation):
 * - All four Sunni schools agree: Yawm al-Shakk = DAY 30 of the outgoing month,
 *   not day 29. Day 29 is the evening of the sighting attempt — that day is
 *   unambiguously within the current month. Day 30 is the structurally doubtful
 *   day that may or may not exist (it exists if no sighting on day-29 evening;
 *   it turns out to have been day 1 of the next month if sighting was missed).
 *   - Hadith Bukhari 1909: "if not sure, complete Sha'ban to 30 days"
 *   - Tirmidhi 686: "fasting the doubtful day disobeys Abū l-Qāsim ﷺ" (re: 30 Sha'bān)
 *   - al-Nawawi (Shafi'i): "the Day of Doubt is the 30th of Sha'bān..."
 * - Calendar from official source (Phase A H1 backbone — Aladhan HJCoSA)
 * - Overlay hesitation badges on day-30 of moon-sighting-sensitive boundaries
 *
 * Per-boundary matrix (mark day-30 of the OUTGOING month):
 * - Sha'bān 30      (month 8)  → "30 Sha'bān OR 1 Ramaḍān (sighting-dependent)"
 * - Ramaḍān 30      (month 9)  → "30 Ramaḍān OR 1 Shawwāl / Eid al-Fitr"
 * - Dhū al-Qaʿdah 30 (month 11) → "30 Dhū al-Qaʿdah OR 1 Dhū al-Ḥijjah / Hajj start"
 * - Dhū al-Ḥijjah 30 (month 12) → "30 Dhū al-Ḥijjah OR 1 Muḥarram / Hijri new year"
 *
 * Even though Saudi authority decrees Dhul-Hijjah start worldwide for Hajj
 * coordination, the structural doubt for the day before exists for everyone
 * (the decree happens AFTER day-29 sunset). The badge is informational, not
 * prescriptive — it tells the user "this day's identity depends on tonight's
 * sighting attempt", which is fiqh-accurate even when the authority is uniform.
 *
 * NOT marked (mid-month, drift averages out for everyone):
 * - Mawlid (12 Rabīʿ I), Laylat al-Qadr (27 Ramaḍān), Ashura (10 Muḥarram)
 *
 * Renders only within ±14 days of today (rolling window) to avoid clutter.
 */

export type HesitationHolidayKey = 'ramadan' | 'eid_fitr' | 'dhul_hijjah' | 'hijri_new_year';

export interface HesitationContext {
  applies: boolean;
  holidayKey?: HesitationHolidayKey;
  /** i18n key for the tooltip text. */
  tooltipKey?: string;
  /** Short label for inline badges (1-2 words). */
  badgeKey?: string;
}

interface HijriDay {
  day: number;
  month: number;
  year: number;
}

const HESITATION_RULES: Array<{
  hijriMonth: number;
  holidayKey: HesitationHolidayKey;
  tooltipKey: string;
  badgeKey: string;
}> = [
  { hijriMonth: 8,  holidayKey: 'ramadan',        tooltipKey: 'hijri_hesitation_ramadan_tooltip',        badgeKey: 'hijri_hesitation_ramadan_badge' },
  { hijriMonth: 9,  holidayKey: 'eid_fitr',       tooltipKey: 'hijri_hesitation_eid_fitr_tooltip',       badgeKey: 'hijri_hesitation_eid_fitr_badge' },
  { hijriMonth: 11, holidayKey: 'dhul_hijjah',    tooltipKey: 'hijri_hesitation_dhul_hijjah_tooltip',    badgeKey: 'hijri_hesitation_dhul_hijjah_badge' },
  { hijriMonth: 12, holidayKey: 'hijri_new_year', tooltipKey: 'hijri_hesitation_hijri_new_year_tooltip', badgeKey: 'hijri_hesitation_hijri_new_year_badge' },
];

/** Midnight-truncate a date to compare calendar days without TZ noise. */
function midnight(d: Date): number {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c.getTime();
}

/**
 * Determine whether a cell should display the hesitation badge.
 *
 * The badge is FORWARD-LOOKING only: it marks a day-30 cell whose identity
 * is still pending the next moon-sighting attempt. Today's and past cells
 * have their Hijri identity already committed by the authoritative source
 * (Aladhan HJCoSA cache → `Intl.DateTimeFormat('islamic-umalqura')`), so
 * showing "this day might be 30 OR might be 1" on a day the cache says is
 * firmly day 30 is misleading.
 *
 * Rules:
 *   - Cell must be a Hijri day 30 of a moon-sighting-sensitive month.
 *   - Cell must be in the FUTURE strictly (cur > today, midnight-truncated).
 *   - Cell must be within `windowDays` ahead of today.
 *
 * @param hijri - The Hijri date for the cell (from Phase A backbone cache).
 * @param gregorian - The Gregorian date for the cell.
 * @param today - Reference "today" (defaults to new Date()).
 * @param windowDays - How many days AHEAD of today to show markers (default 14).
 */
export function getHesitationContext(
  hijri: HijriDay | null | undefined,
  gregorian: Date,
  today: Date = new Date(),
  windowDays = 14,
): HesitationContext {
  // Yawm al-Shakk = day 30 (the structurally doubtful day) per all 4 schools.
  // Day 29 is the sighting-attempt evening, not the doubt day itself.
  if (!hijri || hijri.day !== 30) return { applies: false };
  const rule = HESITATION_RULES.find(r => r.hijriMonth === hijri.month);
  if (!rule) return { applies: false };

  // Forward-only window: today's Hijri identity is already committed by the
  // authoritative source, so the doubt no longer applies to today or past.
  const gMid = midnight(gregorian);
  const tMid = midnight(today);
  if (gMid <= tMid) return { applies: false };
  const daysAhead = Math.floor((gMid - tMid) / 86400000);
  if (daysAhead > windowDays) return { applies: false };

  return {
    applies: true,
    holidayKey: rule.holidayKey,
    tooltipKey: rule.tooltipKey,
    badgeKey: rule.badgeKey,
  };
}
